import { useRef, useState, useEffect } from 'react'
import { isGatewayPayment } from '@/lib/paymentGateway'
import { buildPaymentMethodOptions } from '@/lib/paymentMethodOptions'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import { Button, Card, DateInput, FileInput, Input, Select } from '@/components/ui'
import { PaymentMethodEnableNote } from './PaymentMethodEnableNote'
import { useCreatePayment, useInvoice } from '@/hooks/useInvoices'
import { useUpdatePayment, type PaymentListItem } from '@/hooks/usePayments'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
// WEB-H7: PDF generator is dynamic-imported at the call site (pdfme weight).
import { api } from '@/lib/api'
import type { Payment } from '@/types'
import type { Template } from '@pdfme/common'

// ---------------------------------------------------------------------------
// Internal form shape
// ---------------------------------------------------------------------------

interface PaymentFormData {
  amount: number
  payment_method: string
  paid_at: string
  reference: string
  confirmed_at: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function uploadSlip(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  })
  return presigned.key
}

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface PaymentFieldsProps {
  /**
   * Null → create mode (POST /api/invoices/:invoiceId/payments — modal / wizard / new page).
   * Non-null → edit mode (PATCH /api/payments/:id). For a VERIFIED payment, amount +
   * method are locked (only date + reference editable; re-enforced server-side). EP-82.
   */
  payment: Payment | PaymentListItem | null
  invoiceId: number
  /** Called with the saved Payment after a successful POST. */
  onSave: (saved: Payment) => void
  /**
   * Optional HTML form id. When set, the <form> element receives this id so an
   * external <button form={formId} type="submit"> can trigger submission.
   */
  formId?: string
  /**
   * EP-86 T013 — Streamlined wizard mode (S2-1).
   *
   * false (default): render all fields visible — amount, method, paid_at,
   * reference, and slip upload in a flat space-y-4 layout.
   *
   * true (wizard only — passed by OnboardingWizardPage): Essentials (amount +
   * method) stay inline; paid_at, reference, and slip upload are collapsed
   * into an "Additional details" OptionalSection closed by default. All
   * fields remain in the DOM (display:none) so RHF stays registered and
   * the paid_at default value satisfies required validation without the
   * operator having to open the section (S2-2). Do NOT piggyback this on
   * `formId` (S2-1 decision).
   */
  streamlinedMode?: boolean
  className?: string
  /** Fires with the mutation-pending state so the wizard footer Save can spin. */
  onSubmittingChange?: (submitting: boolean) => void
  /**
   * T131-010 — create-mode-only prefill from the caller's own deep-link query
   * params (e.g. bank reconciliation's "Record payment" CTA carries the bank
   * row's amount + date across via PaymentFormPage's ?amount=&paid_at=).
   * Applied at the SAME one-time seed moment as the existing invoice-balance
   * default (the effect below, gated by hasSeededRef) — takes precedence over
   * invoice.balance_due / today when present, and is never re-applied, so a
   * value the operator has already edited by hand is never overwritten.
   * Ignored in edit mode.
   */
  prefillAmount?: number
  prefillPaidAt?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Self-contained payment form.
 *
 * Owns its own useForm instance. Fetches the invoice to resolve the
 * outstanding balance and the property's configured payment methods.
 *
 * When payment === null the form pre-fills amount with invoice.balance_due
 * and payment_method with the property's (or account's) first enabled method.
 * Save fires POST /api/invoices/:invoiceId/payments.
 *
 * Slip upload uses the Active Storage presigned URL pattern, consistent with
 * expenses, timesheets, and leases.
 *
 * Adds `data-tour-target="payment-amount"` on the amount input so the
 * onboarding wizard spotlight can highlight it.
 */
export function PaymentFields({
  payment,
  invoiceId,
  onSave,
  formId,
  className,
  onSubmittingChange,
  prefillAmount,
  prefillPaidAt,
}: PaymentFieldsProps) {
  const { t, i18n } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')

  const translateError = useApiErrorTranslator('payments')
  const addToast = useNotificationStore((s) => s.addToast)

  const { data: invoice } = useInvoice(invoiceId)
  const { data: accountSettings } = useAccountSettings()
  const { format, timezone, calendar } = useLocalizationConfig()
  const { data: receiptTemplates } = useDocumentTemplates(1, 'receipt')
  const createPayment = useCreatePayment()
  const updatePayment = useUpdatePayment()

  // EP-82 edit mode: a non-null payment → PATCH instead of POST. A verified
  // payment locks amount + method (only paid_at + reference editable; the
  // controller re-enforces this).
  const isEdit = payment !== null
  const isVerifiedLock = payment?.verification_status === 'verified'

  // Slip upload state
  const [slipFile, setSlipFile] = useState<File | null>(null)
  const [slipPreview, setSlipPreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  // Report mutation-pending so the wizard footer Save can show a spinner.
  useEffect(() => {
    onSubmittingChange?.(createPayment.isPending || updatePayment.isPending || isUploading)
  }, [createPayment.isPending, updatePayment.isPending, isUploading, onSubmittingChange])
  const [isPreviewingPdf, setIsPreviewingPdf] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ---------------------------------------------------------------------------
  // Payment method options — prefer property-level config, fall back to account
  // ---------------------------------------------------------------------------

  // Use property-level payment methods if the invoice carries them (and they
  // are non-empty); otherwise fall back to account-level settings.
  const configuredMethods =
    (invoice?.property?.payment_methods &&
      Object.keys(invoice.property.payment_methods).length > 0 &&
      invoice.property.payment_methods) ||
    accountSettings?.settings?.payment_methods

  const paymentMethodOptions = buildPaymentMethodOptions(configuredMethods, (key) =>
    t(`payment.methods.${key}`),
  )

  const defaultMethod = paymentMethodOptions[0]?.value ?? 'cash'

  // ---------------------------------------------------------------------------
  // Form
  // ---------------------------------------------------------------------------

  const today = localToday(timezone)
  // Computed once, up front, so DateInput's own `defaultValue` (register()
  // mode never passes a `value`, so it can't otherwise see the RHF default
  // applied imperatively via ref, see the ui/README.md caveat) reuses the
  // EXACT SAME value as the form's own defaultValues, rather than a second,
  // driftable copy of this ternary.
  const defaultPaidAt = payment !== null ? (payment.paid_at?.split('T')[0] ?? today) : (prefillPaidAt ?? today)
  const defaultConfirmedAt = payment !== null ? (payment.confirmed_at?.split('T')[0] ?? '') : ''

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, dirtyFields },
  } = useForm<PaymentFormData>({
    // Use static defaults first. The useEffect below syncs reactive data once
    // invoice/payment are available. This pattern is required so that RHF
    // correctly initialises the uncontrolled DOM inputs on first render.
    defaultValues:
      payment !== null
        ? {
            amount: payment.amount,
            payment_method: payment.payment_method,
            paid_at: defaultPaidAt,
            reference: payment.reference ?? '',
            confirmed_at: defaultConfirmedAt,
          }
        : {
            // T131-010: the deep-link prefill (if any) wins over the static 0/today
            // fallback on first render; the effect below applies the SAME
            // precedence once the invoice loads (so a caller that fetches
            // invoice/prefill asynchronously still ends up seeded correctly).
            amount: prefillAmount ?? 0,
            payment_method: defaultMethod,
            paid_at: defaultPaidAt,
            reference: '',
            confirmed_at: '',
          },
  })

  // Sync invoice's outstanding balance into the amount field once the invoice
  // is fetched (or immediately in tests where the hook returns synchronously).
  // Only applies when in create mode (payment === null).
  // Also re-syncs the payment method default once paymentMethodOptions are ready.
  const invoiceBalance = invoice?.balance_due
  // Use a stable ref to avoid re-running on every render
  const hasSeededRef = useRef(false)

  useEffect(() => {
    if (payment !== null) return          // edit mode: defaultValues already correct
    if (hasSeededRef.current) return      // already seeded once
    if (invoiceBalance === undefined) return  // invoice not yet loaded

    hasSeededRef.current = true
    // EP-52 F-024: API serialises balance_due as a string with trailing decimal
    // ("5871.0"). Coerce to Number so the type="number" input doesn't render
    // the raw string and surface another trailing-.0 regression.
    // T131-010: prefillAmount/prefillPaidAt (when present) win over the
    // invoice-derived defaults — this only ever runs once (hasSeededRef), so
    // it never clobbers a value the operator has already typed.
    reset({
      amount: prefillAmount ?? (Number(invoiceBalance) || 0),
      payment_method: defaultMethod,
      paid_at: prefillPaidAt ?? today,
      reference: '',
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceBalance, defaultMethod])

  const watchedMethod = watch('payment_method')

  // ---------------------------------------------------------------------------
  // PAY-4 decision C: a non-blocking warning when paid_at predates the
  // invoice's own billed_at, or lands in the future. Plain YYYY-MM-DD string
  // comparison (both sides are date-only, midnight-UTC-of-the-intended-day
  // values per the project's Dates & localization convention, so this is the
  // sanctioned split, not the banned `x.toISOString().slice/split` pattern).
  // ---------------------------------------------------------------------------
  const formatDate = useDateFormatter()
  const watchedPaidAt = watch('paid_at')
  const invoiceBilledDate = invoice?.billed_at?.split('T')[0]
  const paidAtBeforeInvoice = !!(watchedPaidAt && invoiceBilledDate && watchedPaidAt < invoiceBilledDate)
  const paidAtInFuture = !paidAtBeforeInvoice && !!(watchedPaidAt && watchedPaidAt > today)

  // ---------------------------------------------------------------------------
  // Slip upload
  // ---------------------------------------------------------------------------

  const handleSlipChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const { convertToJpegIfNeeded } = await import('@/lib/imageUtils')
      const converted = await convertToJpegIfNeeded(file)
      setSlipFile(converted)
      setSlipPreview(URL.createObjectURL(converted))
    }
  }

  // ---------------------------------------------------------------------------
  // Track the saved payment within this session so re-clicking Preview or
  // Save & Continue doesn't POST a second payment for the same invoice.
  // (Payments are single-shot — no update endpoint is wired through here.)
  // ---------------------------------------------------------------------------
  const [previewSavedPayment, setPreviewSavedPayment] = useState<Payment | null>(null)

  // Shared persistence helper used by both Save & Continue and Preview.
  // Returns the live saved Payment, POSTing only once per session.
  const persistPayment = async (data: PaymentFormData): Promise<Payment> => {
    // EP-82 edit: PATCH the existing payment. No slip re-upload, no single-shot
    // guard (each save patches). A verified payment omits amount/method (locked).
    if (isEdit && payment) {
      const input: {
        id: number
        amount?: number
        payment_method?: string
        paid_at: string
        reference?: string
        confirmed_at?: string | null
      } = {
        id: payment.id,
        paid_at: data.paid_at,
        reference: data.reference || undefined,
      }
      // Only send confirmed_at when the operator actually changed it — otherwise an
      // untouched verified payment would have its precise settlement timestamp
      // truncated to the date input's midnight value on every save. Empty ⇒ null
      // (clears it, un-settling the invoice via the BE recompute).
      if (dirtyFields.confirmed_at) {
        input.confirmed_at = data.confirmed_at ? data.confirmed_at : null
      }
      if (!isVerifiedLock) {
        input.amount = data.amount
        input.payment_method = data.payment_method
      }
      const updated = await updatePayment.mutateAsync(input)
      return updated as unknown as Payment
    }
    if (previewSavedPayment) {
      // Already saved this session — re-use the same record. The form is
      // disabled-ish at this point (re-edits aren't supported), but we
      // tolerate re-clicks instead of throwing.
      return previewSavedPayment
    }
    if (data.payment_method === 'transfer' && !slipFile) {
      throw new Error(t('payment.slip_required'))
    }
    let slip_url: string | undefined
    if (slipFile) {
      slip_url = await uploadSlip(slipFile)
    }
    const result = (await createPayment.mutateAsync({
      invoiceId,
      amount: data.amount,
      payment_method: data.payment_method as 'cash' | 'transfer' | 'promptpay',
      paid_at: data.paid_at,
      reference: data.reference || undefined,
      slip_url,
    })) as { data: Payment & { id?: number }; invoiceId: number }
    const saved: Payment = {
      id: result.data?.id ?? 0,
      invoice_id: invoiceId,
      amount: data.amount,
      payment_method: data.payment_method as Payment['payment_method'],
      paid_at: data.paid_at,
      reference: data.reference || null,
      slip_url: slip_url ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setPreviewSavedPayment(saved)
    return saved
  }

  // ---------------------------------------------------------------------------
  // Preview Receipt PDF — wizard mode. Saves the payment (or re-uses the
  // session-saved one), then opens the rendered receipt PDF in a new tab so
  // the user reviews the real persisted record, not a draft.
  // ---------------------------------------------------------------------------

  const handlePreviewPdf = async () => {
    const tmpl = receiptTemplates?.data?.[0]?.template as Template | undefined
    if (!tmpl) {
      addToast({ type: 'error', message: t('payment.no_receipt_template') })
      return
    }
    if (!invoice) {
      addToast({ type: 'error', message: t('payment.invoice_not_loaded') })
      return
    }
    const values = watch()
    if (!values.amount || values.amount <= 0) return

    setIsPreviewingPdf(true)
    setIsUploading(true)
    try {
      const saved = await persistPayment(values)
      const { previewReceiptPdf } = await import('@/lib/receiptPdfGenerator')
      await previewReceiptPdf(saved, invoice, tmpl, accountSettings)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    } finally {
      setIsPreviewingPdf(false)
      setIsUploading(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Submit — routes through persistPayment() so Save & Continue and Preview
  // share one save path. If Preview already POSTed the payment, this
  // re-uses the saved record and just advances.
  // ---------------------------------------------------------------------------

  const onSubmit = async (data: PaymentFormData) => {
    setIsUploading(true)
    try {
      const saved = await persistPayment(data)
      onSave(saved)
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    } finally {
      setIsUploading(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Advanced fields content — shared between streamlined and standalone layouts.
  // Contains paid_at, reference, and slip upload. Extracted to avoid duplication.
  // ---------------------------------------------------------------------------

  const advancedFieldsContent = (
    <div className="space-y-4">
      <div>
        <DateInput
          label={t('payment.date')}
          error={errors.paid_at?.message}
          defaultValue={defaultPaidAt}
          dateFormat={format}
          timezone={timezone}
          calendar={calendar}
          locale={i18n.language}
          {...register('paid_at', { required: tc('errors.required') })}
        />
        {/* PAY-4 decision C: a warning, never a block. */}
        {paidAtBeforeInvoice && (
          <p className="mt-1 text-xs text-warning" role="note">
            {t('payment.date_before_invoice_warning', { date: formatDate(invoice?.billed_at) })}
          </p>
        )}
        {paidAtInFuture && (
          <p className="mt-1 text-xs text-warning" role="note">
            {t('payment.date_future_warning')}
          </p>
        )}
      </div>

      <Input
        label={t('payment.reference')}
        placeholder={t('payment.reference_placeholder')}
        {...register('reference')}
      />

      {/* Settlement date — edit mode, ONLINE payments only (manual payments count
          toward the invoice regardless of confirmed_at, so the control would be a
          no-op + the hint misleading for them). Editable even when verified (not
          amount/method-locked). Empty = not yet confirmed. */}
      {isEdit && payment && isGatewayPayment(payment) && (
        <div>
          <DateInput
            label={t('payment.confirmed_at')}
            defaultValue={defaultConfirmedAt}
            dateFormat={format}
            timezone={timezone}
            calendar={calendar}
            locale={i18n.language}
            {...register('confirmed_at')}
          />
          <p className="text-xs text-base-content/60 mt-1">{t('payment.confirmed_at_hint')}</p>
        </div>
      )}

      {/* Slip — create mode: upload (required for bank transfer). Edit mode: the
          existing slip is shown read-only as a thumbnail (changing/re-verifying a
          slip goes through the retry-verification flow on the payment detail page). */}
      {isEdit ? (
        payment?.slip_url ? (
          <div>
            <label className="label">
              <span className="label-text">{t('payment.slip')}</span>
            </label>
            <a
              href={`${import.meta.env.VITE_API_URL || ''}/api/images/${payment.slip_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-fit"
              title={tc('actions.view')}
            >
              <img
                src={`${import.meta.env.VITE_API_URL || ''}/api/images/${payment.slip_url}`}
                alt={t('payment.slip')}
                className="max-h-40 rounded border border-base-300 object-contain hover:opacity-90"
              />
            </a>
          </div>
        ) : null
      ) : (
        <div>
          {/* F10/AC-8 (EP-158): the browser's native "Choose File"/"No file
              chosen" text is OS-locale, not CSS-themeable — opt into
              FileInput's overlay so this reads in the account's language. */}
          <FileInput
            ref={fileInputRef}
            label={t('payment.slip')}
            required={watchedMethod === 'transfer'}
            accept="image/*"
            onChange={handleSlipChange}
            chooseLabel={t('payment.slip_choose_file')}
            emptyLabel={t('payment.slip_no_file')}
            selectedFileName={slipFile?.name}
          />
          {slipPreview && (
            <div className="mt-2">
              <img
                src={slipPreview}
                alt={t('payment.slip_preview_alt')}
                className="max-h-32 rounded-lg border border-base-200"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Card className={className}>
    <form
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      noValidate
    >
      {/* Invoice being paid — read-only (chosen in the previous step). */}
      {invoice?.number && (
        <Input
          label={t('labels.invoice_number')}
          value={invoice.number}
          readOnly
          disabled
          data-testid="payment-invoice-number"
        />
      )}

      {/* Essentials — always visible */}
      <Input
        label={t('payment.amount', { currency: tc('currency.thb') })}
        type="number"
        step="0.01"
        disabled={isVerifiedLock}
        error={errors.amount?.message}
        data-tour-target="payment-amount"
        {...register('amount', {
          required: tc('errors.required'),
          valueAsNumber: true,
          min: { value: 0.01, message: tc('errors.positive_number') },
        })}
      />

      {paymentMethodOptions.length > 0 && (
        <Select
          label={t('payment.method')}
          error={errors.payment_method?.message}
          disabled={isVerifiedLock}
          options={paymentMethodOptions}
          {...register('payment_method', { required: tc('errors.required') })}
        />
      )}

      <PaymentMethodEnableNote />

      {isVerifiedLock && (
        <p className="text-xs text-base-content/60">{t('payment.verified_locked_hint')}</p>
      )}

      {/* Additional fields (paid_at, reference, slip upload) — always visible,
          no collapsible. */}
      {advancedFieldsContent}

      {/* Action button:
            • Wizard mode (formId set) → "Preview Receipt PDF" — the wizard's
              own "Save & Continue" still records the payment on advance.
            • Standalone mode → original create submit. */}
      <div className="flex justify-end gap-3 pt-2">
        {formId ? (
          <Button
            type="button"
            variant="outline"
            onClick={handlePreviewPdf}
            loading={isPreviewingPdf}
            leftIcon={<FileText className="w-4 h-4" aria-hidden="true" />}
            data-testid="payment-preview-receipt"
          >
            {t('payment.preview_receipt')}
          </Button>
        ) : (
          <Button
            type="submit"
            loading={createPayment.isPending || updatePayment.isPending || isUploading}
          >
            {isEdit ? tc('actions.save_changes') : t('payment.record')}
          </Button>
        )}
      </div>
    </form>
    </Card>
  )
}
